/**
 * One rider's week, in full, and the one place it is changed.
 *
 * View Details and Edit open the same panel; Edit opens it at the payment
 * form. Everything a week is made of is here, in the order somebody checks
 * it: what the week is, what it charged and why, every payment against it,
 * every change to it, and the rides it was priced on.
 *
 * Nothing here overwrites a payment. A new payment is added beside the others;
 * a wrong one is voided with a reason and stays on the list, struck through.
 * Changing what the week charges keeps the first figure and says who changed
 * it and why. Waiving is shown as Waived with its reason — never as Paid.
 */

import { useState, type ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import {
  PAYMENT_METHODS,
  REMITTANCE_WEEK_STATUS_LABELS,
  VEHICLE_CATEGORY_LABELS,
  addAmounts,
  compareAmounts,
  normalizeAmount,
  type RemittanceWeekDetail,
  type RemittanceWeekRow,
  type RemittanceWeekStatus,
} from '@cenox/shared';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { Badge, Button, Input, Select, cn } from '../../components/ui';
import { newSubmissionKey } from '../loans/data';
import { lateness } from './parts';
import { AccountSelect } from '../money/AccountSelect';
import {
  useChangeRemittanceAmount,
  useChargeWeek,
  useCorrectRemittancePayment,
  useRecordReceipt,
  useRemittanceWeekDetail,
  useSetDueDate,
  useSetRemittanceAgreement,
  useUnwaiveWeek,
  useVoidRemittancePayment,
  useWaiveWeek,
} from './data';

const STATUS_TONES: Record<RemittanceWeekStatus | 'overdue', 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  unpaid: 'info',
  part_paid: 'warning',
  paid: 'success',
  waived: 'neutral',
  not_accrued: 'warning',
  not_required: 'neutral',
  overdue: 'danger',
};

export function WeekStatusBadge({ status }: { status: RemittanceWeekStatus | 'overdue' }): ReactNode {
  return (
    <Badge tone={STATUS_TONES[status]}>{status === 'overdue' ? 'Overdue' : REMITTANCE_WEEK_STATUS_LABELS[status]}</Badge>
  );
}

/** Money in. Credit is not a way of handing over money, so it is not offered. */
const METHODS = PAYMENT_METHODS.filter((m) => m !== 'credit');
const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  card: 'Card',
  mobile_money: 'Mobile money',
  pos_terminal: 'POS terminal',
  cheque: 'Cheque',
  other: 'Other',
};

const ADJUSTMENT_LABELS: Record<string, string> = {
  posted: 'Posted',
  amount_change: 'Amount changed',
  waive: 'Waived',
  unwaive: 'Waiver lifted',
  payment_void: 'Payment voided',
  settled_with_waiver: 'Settled with the shortfall waived',
  due_date_change: 'Due date changed',
  write_off: 'Cleared by the owner (not paid, not revenue)',
};

function Figure({ label, value, tone = 'plain', hint }: { label: string; value: string; tone?: 'plain' | 'good' | 'owed'; hint?: string }): ReactNode {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-base font-semibold tabular-nums',
          tone === 'good' ? 'text-emerald-700' : tone === 'owed' ? 'text-red-700' : 'text-slate-900',
        )}
      >
        {value}
      </p>
      {hint ? <p className="text-[11px] text-slate-400">{hint}</p> : null}
    </div>
  );
}

function Section({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }): ReactNode {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-700">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Where the panel opens: the details, or straight at one of its forms. */
export type PanelMode = 'view' | 'pay' | 'add' | 'amount';

export function RemittanceWeekPanel({
  riderId,
  startsOn,
  mode,
  onClose,
}: {
  riderId: string;
  startsOn: string;
  mode: PanelMode;
  onClose: () => void;
}): ReactNode {
  const detail = useRemittanceWeekDetail(riderId, startsOn);

  return (
    /*
      A fixed panel rather than a modal: the sheet behind it is the context —
      somebody works down a list of riders — and a dialog that blanks the page
      loses their place every time.
    */
    <aside
      role="dialog"
      aria-label="Rider remittance details"
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l border-slate-200 bg-white shadow-2xl"
    >
      {detail.isPending ? (
        <div className="p-6 text-sm text-slate-500">Loading…</div>
      ) : detail.isError ? (
        <div className="p-6">
          <p className="text-sm text-red-700">Could not load this rider's week.</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <PanelBody detail={detail.data} mode={mode} onClose={onClose} />
      )}
    </aside>
  );
}

function PanelBody({
  detail,
  mode,
  onClose,
}: {
  detail: RemittanceWeekDetail;
  mode: PanelMode;
  onClose: () => void;
}): ReactNode {
  const { can } = useAuth();
  const mayCollect = can('remittance:collect');
  const mayManage = can('remittance:manage');
  const { row, week, currencyCode: currency } = detail;

  return (
    <>
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-slate-900">{row.riderName}</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {row.riderPhone ?? 'No phone on file'}
            {row.vehicleRegistration ? ` · ${row.vehicleRegistration}` : ' · no vehicle recorded'}
            {row.vehicleCategory ? ` · ${VEHICLE_CATEGORY_LABELS[row.vehicleCategory].split(' / ')[0]}` : ''}
            {row.branchName ? ` · ${row.branchName}` : ''}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            <WeekStatusBadge status={row.status} />
            {row.overdue ? <WeekStatusBadge status="overdue" /> : null}
            {row.riderStatus !== 'active' ? <Badge tone="neutral">{row.riderStatus === 'inactive' ? 'Former rider' : row.riderStatus}</Badge> : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X className="size-5" aria-hidden />
        </button>
      </header>

      <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
        <section>
          <p className="text-sm font-medium text-slate-700">{week.label}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {week.startsOn} → {week.endsOn} · due {row.dueOn} · {detail.graceDays}-day grace · overdue after{' '}
            {row.overdueAfter}
            {row.posted && row.remaining !== '0.00' ? ` · ${lateness(row.daysLate)}` : ''}
          </p>
          {week.open ? (
            <p className="mt-1 text-xs text-amber-700">The week is still open: unposted figures are provisional.</p>
          ) : null}
        </section>

        <section className="grid grid-cols-3 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <Figure
            label={row.provisional ? 'Due (provisional)' : 'Amount due'}
            value={money(row.amountDue, currency)}
            hint={row.adjusted && row.originalAmount ? `Originally ${money(row.originalAmount, currency)}` : undefined}
          />
          <Figure label="Paid" value={money(row.amountPaid, currency)} tone="good" />
          <Figure label="Remaining" value={money(row.remaining, currency)} tone={row.remaining !== '0.00' ? 'owed' : 'plain'} />
          {row.waivedAmount !== '0.00' ? (
            <Figure label="Waived" value={money(row.waivedAmount, currency)} hint={row.settlementReason ?? undefined} />
          ) : null}
          <Figure label="Earlier weeks" value={money(row.arrearsBefore, currency)} tone={row.arrearsBefore !== '0.00' ? 'owed' : 'plain'} hint="Kept apart from this week" />
          <Figure label="All weeks outstanding" value={money(detail.totalOutstanding, currency)} tone={detail.totalOutstanding !== '0.00' ? 'owed' : 'plain'} />
        </section>

        <SplitSection split={row.split} currency={currency} />

        <Section title="How the amount was worked out">
          {row.calculation ? (
            <p className="text-sm text-slate-700">{row.calculation.description}</p>
          ) : (
            <p className="text-sm text-slate-500">
              No remittance is required for this week.
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500">
            {row.completedRides} completed {row.completedRides === 1 ? 'ride' : 'rides'} this week
            {row.postedRides !== null && row.postedRides !== row.completedRides ? ` (${row.postedRides} when posted)` : ''}.
          </p>
          {row.conflicts.length > 0 ? (
            <ul className="mt-2 space-y-1.5">
              {row.conflicts.map((c) => (
                <li key={c} className="flex gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  {c}
                </li>
              ))}
            </ul>
          ) : null}
        </Section>

        {row.posted && row.remittanceId ? (
          <EditSection detail={detail} mayCollect={mayCollect} mayManage={mayManage} openPayment={mode === 'pay'} editAmount={mode === 'amount'} />
        ) : row.status === 'not_accrued' ? (
          <>
            {mayCollect ? (
              <PaymentForm detail={detail} openInitially={mode === 'pay'} />
            ) : (
              <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                This week has not been posted. Someone allowed to record remittance can add the first payment, which
                charges the week for this rider.
              </p>
            )}
            {mayManage ? <ChargeSection detail={detail} openInitially={mode === 'add'} /> : null}
          </>
        ) : row.status === 'not_required' && !row.remittanceId && mayManage && row.settlementReason === null ? (
          <ChargeSection detail={detail} openInitially={mode === 'add'} />
        ) : null}

        <Payments detail={detail} />

        <Section title={`Change history (${detail.adjustments.length})`}>
          {detail.adjustments.length === 0 ? (
            <p className="text-sm text-slate-500">No changes to this week.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {detail.adjustments.map((a) => (
                <li key={a.id} className="px-3 py-2 text-sm">
                  <p className="font-medium text-slate-800">
                    {ADJUSTMENT_LABELS[a.kind] ?? a.kind}
                    {a.amountBefore !== null && a.amountAfter !== null && a.amountBefore !== a.amountAfter
                      ? `: ${money(a.amountBefore, currency)} → ${money(a.amountAfter, currency)}`
                      : a.dueOnBefore && a.dueOnAfter
                        ? `: ${a.dueOnBefore} → ${a.dueOnAfter}`
                        : ''}
                  </p>
                  <p className="text-xs text-slate-500">
                    {new Date(a.createdAt).toLocaleString()} · {a.recordedBy ?? 'Unknown'}
                    {a.reason ? ` · ${a.reason}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={`Completed deliveries (${detail.deliveries.length})`}>
          {detail.deliveries.length === 0 ? (
            <p className="text-sm text-slate-500">No completed deliveries this week.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 font-medium">Date</th>
                    <th className="px-2 py-1.5 font-medium">Reference</th>
                    <th className="px-2 py-1.5 font-medium">Source</th>
                    <th className="px-2 py-1.5 text-right font-medium">Fee</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {detail.deliveries.map((d) => (
                    <tr key={d.id}>
                      <td className="px-2 py-1.5">{d.completedOn}</td>
                      <td className="px-2 py-1.5">{d.reference ?? '—'}</td>
                      <td className="px-2 py-1.5">{d.source}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{money(d.fee, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Agreements detail={detail} mayManage={mayManage} />
      </div>

      <footer className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-3">
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </footer>
    </>
  );
}

// ── Recording money, changing the charge, waiving ──────────────────────────

function EditSection({
  detail,
  mayCollect,
  mayManage,
  openPayment,
  editAmount,
}: {
  detail: RemittanceWeekDetail;
  mayCollect: boolean;
  mayManage: boolean;
  openPayment: boolean;
  editAmount: boolean;
}): ReactNode {
  const { row, currencyCode: currency } = detail;
  const remittanceId = row.remittanceId!;

  const change = useChangeRemittanceAmount();
  const waive = useWaiveWeek();
  const unwaive = useUnwaiveWeek();

  const [newAmount, setNewAmount] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [waiveReason, setWaiveReason] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const settled = row.remaining === '0.00';
  const waived = row.status === 'waived';
  const failure = change.error ?? waive.error ?? unwaive.error;
  const fields = failure instanceof ApiError ? failure.fields : undefined;

  return (
    <div className="space-y-5">
      {failure ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{failure instanceof ApiError ? failure.message : 'That did not work.'}</p>
        </div>
      ) : null}
      {done ? <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{done}</p> : null}

      {mayCollect && !waived && !settled ? <PaymentForm detail={detail} openInitially={openPayment} /> : null}

      {mayManage && !waived ? (
        <Section title="Edit remittance">
          <form
            className="space-y-3"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              change.mutate(
                { id: remittanceId, amount: normalizeAmount(newAmount), reason: changeReason },
                {
                  onSuccess: () => {
                    setNewAmount('');
                    setChangeReason('');
                    setDone('Amount changed. The original figure is kept in the history.');
                  },
                },
              );
            }}
          >
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={`New amount due (${currency})`}
                inputMode="decimal"
                autoFocus={editAmount}
                value={newAmount}
                onChange={(event) => setNewAmount(event.target.value)}
                hint={`Now ${money(row.amountDue, currency)}`}
              />
              <Input
                label="Reason"
                value={changeReason}
                onChange={(event) => setChangeReason(event.target.value)}
                hint="Required, kept on the record"
                error={fields?.reason?.[0]}
              />
            </div>
            <Button type="submit" variant="secondary" size="sm" loading={change.isPending} disabled={!newAmount || changeReason.trim().length < 3}>
              Save new amount
            </Button>
          </form>
        </Section>
      ) : null}

      {mayManage ? <DueDateForm detail={detail} /> : null}

      {mayManage ? (
        <Section title="Waiver">
          {waived ? (
            <>
              <p className="text-sm text-slate-700">Waived{row.settlementReason ? `: ${row.settlementReason}` : ''}</p>
              <Button variant="secondary" size="sm" className="mt-2" loading={unwaive.isPending} onClick={() => unwaive.mutate(remittanceId)}>
                Lift the waiver
              </Button>
            </>
          ) : row.amountPaid !== '0.00' ? (
            <p className="text-xs text-slate-500">
              Money has been paid against this week. To forgive the rest, change the amount due to what was paid, with a
              reason.
            </p>
          ) : (
            <form
              className="flex items-end gap-2"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                waive.mutate({ id: remittanceId, reason: waiveReason }, { onSuccess: () => setWaiveReason('') });
              }}
            >
              <div className="flex-1">
                <Input
                  label="Why is it waived?"
                  value={waiveReason}
                  onChange={(event) => setWaiveReason(event.target.value)}
                  hint="Forgives the week. It shows as Waived, never as Paid."
                />
              </div>
              <Button type="submit" variant="danger" size="sm" loading={waive.isPending} disabled={waiveReason.trim().length < 3}>
                Waive
              </Button>
            </form>
          )}
        </Section>
      ) : null}
    </div>
  );
}

/**
 * Add one payment to this week.
 *
 * Always a NEW payment, added to what has already been paid — never a figure
 * that replaces it. On a week nobody has posted, saving the first payment
 * charges the week for this rider (the amount the sheet shows, by the same
 * rules posting uses) and records the payment against it, in one step.
 */
function PaymentForm({ detail, openInitially }: { detail: RemittanceWeekDetail; openInitially: boolean }): ReactNode {
  const { business } = useAuth();
  const timezone = business?.timezone ?? 'Africa/Lagos';
  const { row, week, currencyCode: currency } = detail;
  const charged = row.remittanceId !== null;
  // This week's line: its charge, or — unposted — the day it starts.
  const thisKey = row.remittanceId ?? `week:${week.startsOn}`;

  const record = useRecordReceipt();

  const [open, setOpen] = useState(openInitially);
  // One key per form: a retried or double-clicked submit is the same payment, not a second one.
  const [submissionKey, setSubmissionKey] = useState(newSubmissionKey);
  const [amount, setAmount] = useState(openInitially && row.remaining !== '0.00' ? row.remaining : '');
  const [paidOn, setPaidOn] = useState(todayIn(timezone));
  const [method, setMethod] = useState<(typeof METHODS)[number]>('cash');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [accountId, setAccountId] = useState('');
  const [split, setSplit] = useState(false);
  const [shares, setShares] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);

  const others = detail.outstandingWeeks.filter((w) => w.remittanceId !== row.remittanceId);
  const thisShare = split ? (shares[thisKey] ?? '') : amount;
  const lines = split
    ? Object.entries(shares)
        .filter(([, v]) => v.trim() !== '' && Number(v) > 0)
        .map(([key, v]) => ({ key, amount: v }))
    : [{ key: thisKey, amount }];
  let splitTotal = '0.00';
  try {
    splitTotal = lines.reduce((sum, a) => addAmounts(sum, normalizeAmount(a.amount)), '0.00');
  } catch {
    splitTotal = 'invalid';
  }
  const splitMismatch = split && amount !== '' && splitTotal !== 'invalid' && compareAmounts(splitTotal, normalizeAmount(amount || 0)) !== 0;

  const fields = record.error instanceof ApiError ? record.error.fields : undefined;
  const looksDuplicate = record.error instanceof ApiError && Boolean(record.error.fields?.confirmDuplicate);

  const submit = (confirmDuplicate: boolean) => {
    // A second press while the first is on its way is the same payment.
    if (record.isPending) return;
    setDone(null);
    record.mutate(
      {
        riderId: row.riderId,
        amount: normalizeAmount(amount),
        paidOn,
        method,
        idempotencyKey: submissionKey,
        allocations: lines.map((l) =>
          l.key.startsWith('week:')
            ? { weekStartsOn: l.key.slice(5), amount: normalizeAmount(l.amount) }
            : { remittanceId: l.key, amount: normalizeAmount(l.amount) },
        ),
        ...(reference ? { reference } : {}),
        ...(note ? { note } : {}),
        ...(accountId ? { accountId } : {}),
        ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
      },
      {
        onSuccess: (result) => {
          setDone(result.data.replayed ? 'That payment was already recorded.' : 'Payment recorded.');
          setAmount('');
          setReference('');
          setNote('');
          setShares({});
          setSplit(false);
          setOpen(false);
          setSubmissionKey(newSubmissionKey());
        },
      },
    );
  };

  return (
    <Section
      title="Record money received"
      aside={
        open ? null : (
          <Button size="sm" onClick={() => { record.reset(); setDone(null); setOpen(true); }}>
            Add payment
          </Button>
        )
      }
    >
      {done ? <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{done}</p> : null}
      {!open ? (
        <p className="text-xs text-slate-500">
          {money(row.amountPaid, currency)} paid of {money(row.amountDue, currency)}
          {row.provisional ? ' (provisional)' : ''} · {money(row.remaining, currency)} remaining.
        </p>
      ) : (
        <form
          className="space-y-3"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submit(false);
          }}
        >
          {!charged ? (
            <p className="rounded-lg border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-900">
              This week has not been posted. Saving this payment first charges {row.riderName}{' '}
              {money(row.amountDue, currency)} for the week
              {row.calculation ? ` (${row.calculation.description})` : ''}
              {week.open ? ', provisionally until the week ends' : ''}, then records the payment against it.
            </p>
          ) : null}
          {record.error ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
              <p className="text-sm text-red-800">{record.error instanceof ApiError ? record.error.message : 'That did not work.'}</p>
              {looksDuplicate ? (
                <Button size="sm" variant="secondary" className="mt-2" loading={record.isPending} onClick={() => submit(true)}>
                  It is a separate payment — record it
                </Button>
              ) : null}
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={`Amount of this payment (${currency})`}
              inputMode="decimal"
              required
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              error={fields?.amount?.[0]}
              hint={`A new payment, added to the ${money(row.amountPaid, currency)} already paid. ${money(row.remaining, currency)} remaining.`}
            />
            <Input
              label="Payment date"
              type="date"
              required
              value={paidOn}
              max={todayIn(timezone)}
              onChange={(event) => setPaidOn(event.target.value)}
              error={fields?.paidOn?.[0]}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Method"
              value={method}
              onChange={(event) => {
                setMethod(event.target.value as (typeof METHODS)[number]);
                setAccountId('');
              }}
              options={METHODS.map((value) => ({ value, label: METHOD_LABELS[value] ?? value }))}
            />
            <Input label="Reference" value={reference} onChange={(event) => setReference(event.target.value)} hint="Optional · transfer or receipt number" />
          </div>
          <AccountSelect method={method} value={accountId} onChange={setAccountId} error={fields?.accountId?.[0]} />
          <Input label="Note" value={note} onChange={(event) => setNote(event.target.value)} hint="Optional" />

          {others.length > 0 ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={split}
                  onChange={(event) => {
                    setSplit(event.target.checked);
                    setShares(event.target.checked ? { [thisKey]: amount } : {});
                  }}
                />
                Split this payment across weeks
              </label>
              {split ? (
                <div className="mt-3 space-y-2">
                  {[{ key: thisKey, label: `${week.label} (this week)`, remaining: row.remaining }, ...others.map((w) => ({ key: w.remittanceId, label: w.label, remaining: w.remaining }))].map((w) => (
                    <div key={w.key} className="flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-600">
                        {w.label} · owes {money(w.remaining, currency)}
                      </span>
                      <input
                        aria-label={`Amount for ${w.label}`}
                        inputMode="decimal"
                        className="w-28 rounded-lg border border-slate-300 px-2 py-1 text-right text-sm tabular-nums"
                        value={w.key === thisKey ? thisShare : (shares[w.key] ?? '')}
                        onChange={(event) => setShares({ ...shares, [w.key]: event.target.value })}
                      />
                    </div>
                  ))}
                  <p className={cn('text-xs', splitMismatch ? 'text-red-700' : 'text-slate-500')}>
                    Allocated {splitTotal === 'invalid' ? '—' : money(splitTotal, currency)} of{' '}
                    {money(amount ? normalizeAmount(amount) : '0', currency)}. The weeks must add up to the amount received.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)} disabled={record.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={record.isPending} disabled={!amount || splitMismatch || record.isPending} className="flex-1">
              Save payment
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
}

/**
 * Charge this rider for the week by hand, before the week is posted.
 *
 * At the figure the rules give, or at another figure with a reason — which is
 * also how a rider the rules would not charge is charged. Posting the week
 * later charges everyone else and leaves this charge where it is.
 */
function ChargeSection({ detail, openInitially = false }: { detail: RemittanceWeekDetail; openInitially?: boolean }): ReactNode {
  const { row, week, currencyCode: currency } = detail;
  const charge = useChargeWeek();
  const priced = row.status === 'not_accrued' ? row.amountDue : null;
  const [open, setOpen] = useState(openInitially);
  const [amount, setAmount] = useState(priced ?? '');
  const [reason, setReason] = useState('');
  let differs = true;
  try {
    differs = priced === null || amount.trim() === '' || compareAmounts(normalizeAmount(amount), priced) !== 0;
  } catch {
    differs = true;
  }
  const fields = charge.error instanceof ApiError ? charge.error.fields : undefined;

  return (
    <Section
      title="Add remittance"
      aside={open ? null : <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Add Remittance</Button>}
    >
      {!open ? (
        <p className="text-xs text-slate-500">
          {priced
            ? `Charge ${row.riderName} for this week now, without waiting to post it or for a payment.`
            : `The rules charge ${row.riderName} nothing for this week. A manager can still add a charge, with a reason.`}
        </p>
      ) : (
        <form
          className="space-y-3"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            charge.mutate(
              {
                riderId: row.riderId,
                startsOn: week.startsOn,
                ...(amount.trim() !== '' ? { amount: normalizeAmount(amount) } : {}),
                ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
              },
              { onSuccess: () => setOpen(false) },
            );
          }}
        >
          {charge.error ? (
            <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {charge.error instanceof ApiError ? charge.error.message : 'Could not add the charge.'}
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={`Weekly remittance (${currency})`}
              autoFocus
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              error={fields?.amount?.[0]}
              hint={priced ? `The rules give ${money(priced, currency)}` : 'Required'}
            />
            <Input
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              error={fields?.reason?.[0]}
              hint={differs ? 'Required for an amount set by hand' : 'Optional'}
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)} disabled={charge.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={charge.isPending}
              disabled={amount.trim() === '' || (differs && reason.trim().length < 3)}
              className="flex-1"
            >
              Save remittance
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
}

/** Give this rider's week its own due date, with a reason kept in the history. */
function DueDateForm({ detail }: { detail: RemittanceWeekDetail }): ReactNode {
  const { row, week } = detail;
  const setDue = useSetDueDate();
  const [dueOn, setDueOn] = useState(row.overdueAfter);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState(false);
  const fields = setDue.error instanceof ApiError ? setDue.error.fields : undefined;

  return (
    <Section title="Due date">
      <form
        className="space-y-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setDone(false);
          setDue.mutate(
            { id: row.remittanceId!, dueOn, reason },
            { onSuccess: () => { setReason(''); setDone(true); } },
          );
        }}
      >
        {setDue.error ? (
          <p role="alert" className="text-xs text-red-700">
            {setDue.error instanceof ApiError ? setDue.error.message : 'Could not change the due date.'}
          </p>
        ) : null}
        {done ? <p className="text-xs text-emerald-700">Due date changed. The old date is kept in the history.</p> : null}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Overdue after"
            type="date"
            value={dueOn}
            min={week.startsOn}
            onChange={(event) => setDueOn(event.target.value)}
            error={fields?.dueOn?.[0]}
            hint={`Now ${row.overdueAfter}`}
          />
          <Input
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={fields?.reason?.[0]}
            hint="Required, kept on the record"
          />
        </div>
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          loading={setDue.isPending}
          disabled={dueOn === row.overdueAfter || reason.trim().length < 3}
        >
          Change the due date
        </Button>
      </form>
    </Section>
  );
}

// ── Payments ────────────────────────────────────────────────────────────────

function Payments({ detail }: { detail: RemittanceWeekDetail }): ReactNode {
  const currency = detail.currencyCode;
  const voidPayment = useVoidRemittancePayment();
  const [voiding, setVoiding] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const live = detail.allocations.filter((a) => !a.voided);

  return (
    <Section title={`Payments toward this week (${live.length})`}>
      {voidPayment.error ? (
        <p role="alert" className="mb-2 text-xs text-red-700">
          {voidPayment.error instanceof ApiError ? voidPayment.error.message : 'Could not void the payment.'}
        </p>
      ) : null}
      {detail.allocations.length === 0 ? (
        <p className="text-sm text-slate-500">
          {detail.row.settledBy === 'legacy'
            ? 'Settled before payments were recorded one by one, so there are no instalments to show.'
            : 'Nothing received against this week yet.'}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {detail.allocations.map((p) => (
            <li key={p.id} className={cn('px-3 py-2.5', p.voided && 'bg-slate-50')}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={cn('text-sm font-medium tabular-nums text-slate-900', p.voided && 'line-through text-slate-400')}>
                    {money(p.amount, currency)}
                    {p.receiptAmount && p.receiptAmount !== p.amount ? (
                      <span className="ml-1.5 text-xs font-normal text-slate-500">
                        of a {money(p.receiptAmount, currency)} payment split across weeks
                      </span>
                    ) : null}
                    {p.investorAmount !== '0.00' ? (
                      <span className="ml-1.5 text-xs font-normal text-slate-500">
                        · {money(p.investorAmount, currency)} investor · {money(p.companyAmount, currency)} company
                      </span>
                    ) : null}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {p.paidOn}
                    {p.method ? ` · ${METHOD_LABELS[p.method] ?? p.method.replace(/_/g, ' ')}` : ''}
                    {p.accountName ? ` · ${p.accountName}` : ''}
                    {p.reference ? ` · ref ${p.reference}` : ''}
                    {p.recordedBy ? ` · recorded by ${p.recordedBy}` : ''}
                  </p>
                  {p.note ? <p className="mt-0.5 text-xs text-slate-400">{p.note}</p> : null}
                  {p.voided ? (
                    <p className="mt-0.5 text-xs text-red-700">
                      Voided{p.voidedBy ? ` by ${p.voidedBy}` : ''}{p.voidedAt ? ` on ${new Date(p.voidedAt).toLocaleDateString()}` : ''}: {p.voidReason}
                    </p>
                  ) : null}
                </div>
                {p.mayVoid && voiding !== p.id && correcting !== p.id ? (
                  <div className="flex shrink-0 gap-1">
                    {/* A split payment is corrected by voiding it and recording it again, across its weeks. */}
                    {p.receiptId && (p.receiptAmount === null || p.receiptAmount === p.amount) && detail.row.remittanceId ? (
                      <Button variant="ghost" size="sm" onClick={() => { setCorrecting(p.id); setVoiding(null); }}>
                        Correct
                      </Button>
                    ) : null}
                    <Button variant="ghost" size="sm" onClick={() => { setVoiding(p.id); setCorrecting(null); setReason(''); }}>
                      Void
                    </Button>
                  </div>
                ) : null}
              </div>
              {correcting === p.id ? (
                <CorrectPayment detail={detail} payment={p} onDone={() => setCorrecting(null)} />
              ) : null}
              {voiding === p.id ? (
                <form
                  className="mt-2 flex items-end gap-2"
                  noValidate
                  onSubmit={(event) => {
                    event.preventDefault();
                    voidPayment.mutate(
                      { receiptId: p.receiptId, paymentId: p.id, reason },
                      { onSuccess: () => setVoiding(null) },
                    );
                  }}
                >
                  <div className="flex-1">
                    <Input
                      label="Why is this payment wrong?"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      hint={p.receiptAmount && p.receiptAmount !== p.amount ? 'Voids the whole split payment, every week it paid.' : 'The payment stays on the record, marked void.'}
                    />
                  </div>
                  <Button type="button" variant="secondary" size="sm" onClick={() => setVoiding(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="danger" size="sm" loading={voidPayment.isPending} disabled={reason.trim().length < 3}>
                    Void payment
                  </Button>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/**
 * Correct one payment: the form starts from what was recorded; saving voids
 * that payment and records this one, together, with the reason on both.
 */
function CorrectPayment({
  detail,
  payment,
  onDone,
}: {
  detail: RemittanceWeekDetail;
  payment: RemittanceWeekDetail['allocations'][number];
  onDone: () => void;
}): ReactNode {
  const { business } = useAuth();
  const timezone = business?.timezone ?? 'Africa/Lagos';
  const currency = detail.currencyCode;
  const correct = useCorrectRemittancePayment();
  const [key] = useState(newSubmissionKey);
  const [amount, setAmount] = useState(payment.amount);
  const [paidOn, setPaidOn] = useState(payment.paidOn);
  const initialMethod = (METHODS as readonly string[]).includes(payment.method ?? '') ? (payment.method as (typeof METHODS)[number]) : 'cash';
  const [method, setMethod] = useState<(typeof METHODS)[number]>(initialMethod);
  const [reference, setReference] = useState(payment.reference ?? '');
  const [reason, setReason] = useState('');
  const fields = correct.error instanceof ApiError ? correct.error.fields : undefined;

  return (
    <form
      className="mt-2 space-y-3 rounded-lg border border-slate-200 bg-white p-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (correct.isPending) return;
        correct.mutate(
          {
            receiptId: payment.receiptId!,
            reason,
            payment: {
              riderId: detail.row.riderId,
              amount: normalizeAmount(amount),
              paidOn,
              method,
              idempotencyKey: key,
              allocations: [{ remittanceId: detail.row.remittanceId!, amount: normalizeAmount(amount) }],
              ...(reference ? { reference } : {}),
              confirmDuplicate: true,
            },
          },
          { onSuccess: onDone },
        );
      }}
    >
      <p className="text-xs text-slate-500">
        The {money(payment.amount, currency)} payment is voided and kept on the record; the corrected one is recorded
        beside it.
      </p>
      {correct.error ? (
        <p role="alert" className="text-xs text-red-700">
          {correct.error instanceof ApiError ? correct.error.message : 'Could not correct the payment.'}
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <Input label={`Correct amount (${currency})`} inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} error={fields?.amount?.[0]} />
        <Input label="Payment date" type="date" value={paidOn} max={todayIn(timezone)} onChange={(event) => setPaidOn(event.target.value)} error={fields?.paidOn?.[0]} />
        <Select
          label="Method"
          value={method}
          onChange={(event) => setMethod(event.target.value as (typeof METHODS)[number])}
          options={METHODS.map((value) => ({ value, label: METHOD_LABELS[value] ?? value }))}
        />
        <Input label="Reference" value={reference} onChange={(event) => setReference(event.target.value)} hint="Optional" />
      </div>
      <Input label="What was wrong?" value={reason} onChange={(event) => setReason(event.target.value)} error={fields?.reason?.[0]} hint="Required, kept on the record" />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onDone} disabled={correct.isPending}>
          Cancel
        </Button>
        <Button type="submit" size="sm" loading={correct.isPending} disabled={!amount || reason.trim().length < 3}>
          Save correction
        </Button>
      </div>
    </form>
  );
}

// ── Agreements ──────────────────────────────────────────────────────────────

function Agreements({ detail, mayManage }: { detail: RemittanceWeekDetail; mayManage: boolean }): ReactNode {
  const currency = detail.currencyCode;
  const set = useSetRemittanceAgreement();
  const [adding, setAdding] = useState(false);
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState(detail.week.startsOn);
  const [reason, setReason] = useState('');

  return (
    <Section
      title="Individual agreement"
      aside={
        mayManage && !adding ? (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            Set an agreement
          </Button>
        ) : null
      }
    >
      {detail.agreements.length === 0 ? (
        <p className="text-sm text-slate-500">No individual agreement. The business's rules decide this rider's figure.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {detail.agreements.map((a) => (
            <li key={a.id} className="px-3 py-2 text-sm">
              <p className="font-medium text-slate-800">
                {money(a.amount, currency)} a week {a.bikeId ? '(this vehicle)' : '(this rider)'}
              </p>
              <p className="text-xs text-slate-500">
                From {a.effectiveFrom}
                {a.effectiveTo ? ` to ${a.effectiveTo}` : ', still in force'} · {a.reason}
              </p>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <form
          className="mt-3 space-y-3 rounded-lg border border-slate-200 p-3"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            set.mutate(
              { riderId: detail.row.riderId, amount: normalizeAmount(amount), effectiveFrom: from, reason },
              { onSuccess: () => { setAdding(false); setAmount(''); setReason(''); } },
            );
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <Input label={`Weekly amount (${currency})`} inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
            <Input label="From" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <Input label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} hint="What was agreed, and with whom" />
          {set.error ? (
            <p role="alert" className="text-xs text-red-700">
              {set.error instanceof ApiError ? set.error.message : 'Could not save the agreement.'}
            </p>
          ) : null}
          <p className="text-xs text-slate-500">
            An agreement takes precedence over the business's rules from its start date. Weeks already posted keep what
            they charged.
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" loading={set.isPending} disabled={!amount || reason.trim().length < 3}>
              Save agreement
            </Button>
          </div>
        </form>
      ) : null}
    </Section>
  );
}

/**
 * Whose the week's money is. For a company bike all of it is the company's;
 * for an investor's, the investor's share comes first up to their entitlement
 * and every payment is divided in proportion — each figure here is fixed by
 * the payments themselves, so collected and outstanding always add up.
 */
function SplitSection({ split, currency }: { split: RemittanceWeekRow['split']; currency: string }): ReactNode {
  const ownerText =
    split.ownership === null
      ? 'No vehicle recorded for this week — all of it is the company’s.'
      : split.ownership === 'investor'
        ? `Investor-owned${split.investorName ? ` (${split.investorName})` : ''}.`
        : split.ownership === 'rider'
          ? 'Rider-owned: no weekly bike remittance.'
          : 'Company-owned: all of it is the company’s.';
  return (
    <Section title="Investor and company share">
      <p className="text-sm text-slate-700">
        {ownerText}
        {split.legacy && split.ownership === 'investor'
          ? ' Charged before ownership was tracked, so this week stays all company.'
          : !split.fixed && split.ownership === 'investor'
            ? ' Projected: the split is fixed when the week is charged.'
            : ''}
      </p>
      {Number(split.investorEntitlement) > 0 ? (
        <div className="mt-2 -mx-1 overflow-x-auto px-1">
          <table className="w-full min-w-[360px] text-sm" aria-label="Investor and company share">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-1 pr-3 font-medium" />
                <th className="py-1 pr-3 text-right font-medium">Entitled</th>
                <th className="py-1 pr-3 text-right font-medium">Collected</th>
                <th className="py-1 text-right font-medium">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="py-1.5 pr-3 text-slate-600">Investor</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{money(split.investorEntitlement, currency)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{money(split.investorCollected, currency)}</td>
                <td className="py-1.5 text-right tabular-nums">{money(split.investorOutstanding, currency)}</td>
              </tr>
              <tr>
                <td className="py-1.5 pr-3 text-slate-600">Company</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{money(split.companyEntitlement, currency)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{money(split.companyCollected, currency)}</td>
                <td className="py-1.5 text-right tabular-nums">{money(split.companyOutstanding, currency)}</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-1 text-xs text-slate-500">
            Each payment is divided in proportion: {money(split.investorEntitlement, currency)} of every{' '}
            {money(String(Number(split.investorEntitlement) + Number(split.companyEntitlement)), currency)} to the
            investor, and the payment that completes the week settles the exact remainder. The investor’s collected
            share is owed to them, not company revenue.
          </p>
          {split.heldDays !== null && split.heldDays !== undefined && split.sharedDays ? (
            <p className="mt-1 text-xs text-slate-500">
              The bike changed riders this week: this rider held it {split.heldDays} of {split.sharedDays} days, so this
              is their part of the investor’s weekly share.
            </p>
          ) : null}
        </div>
      ) : null}
      {split.review ? (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">
          {split.review}
        </p>
      ) : null}
    </Section>
  );
}
